<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="900px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Claim Assignee"
      text="Edit Claim Assignee"
      class="mb-0"
    >
      <form v-if="dialog">
        <v-container>
          <v-row no-gutters>
            <v-col cols="12">
              <v-text-field
                v-model="approvalLevel"
                v-validate="'required|min_value:1|cannotBeSuperiorLevel|maxValue'"
                :min="1"
                :max="claimApprovalWorkflowBatch.total_level_of_approval"
                :error-messages="errors.collect('approval_level')"
                :readonly="!!claimAssignee.employee.end_date"
                type="number"
                data-vv-name="approval_level"
                data-vv-as="approval level"
                label="Approval Level"
                @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
              />
            </v-col>

            <v-col cols="12">
              <v-autocomplete 
                v-model="localClaimAssignee.employee_uid"
                v-validate="'required'"
                :items="filteredEmployees"
                :error-messages="errors.collect('employee_uid')"
                :readonly="!!claimAssignee.employee.end_date"
                data-vv-name="employee_uid"      
                data-vv-as="employee"
                label="Employee"
                item-text="person.name"
                item-value="employee_uid"     
              />
            </v-col>

            <v-col
              cols="12"
              class="py-0"
            >
              <v-checkbox
                v-model="localClaimAssignee.enable_threshold"
                :disabled="!!claimAssignee.employee.end_date"
                color="green"
                label="Enable threshold?"
              />
              <div class="text-caption text--secondary ml-8 mt-n6">
                By enabling threshold, you are required to set the threshold rule. This approver will only apply when one of the claim request entry meet one of the threshold rules.
              </div>
            </v-col>

            <v-col
              v-if="localClaimAssignee.enable_threshold"
              cols="11"
              class="mx-8 my-2"
            >
              <v-row>
                <v-col>
                  <span class="font-weight-bold pr-2">Threshold Rule:</span>
                  <v-tooltip
                    v-if="localClaimAssignee.claim_threshold_rules.length < types.length && !claimAssignee.employee.end_date"
                    bottom
                  >
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon
                        v-if="localClaimAssignee.claim_threshold_rules.length < types.length"
                        color="primary"
                        v-on="{ ...dialog, ...tooltip }"
                        @click="addRow"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>Add new rule</span>
                  </v-tooltip>
                </v-col>
              </v-row>

              <v-row
                v-for="(rule,counter) in localClaimAssignee.claim_threshold_rules"
                :key="counter"
              >
                <v-col
                  cols="3"
                  class="py-0"
                >
                  <v-autocomplete
                    v-model="rule.type"
                    v-validate="'required'"
                    :name="'type' + counter"
                    :error-messages="errors.collect('type' + counter)"
                    :items="thresholdTypes(counter)"
                    :disabled="!!claimAssignee.employee.end_date"
                    data-vv-delay="500"
                    data-vv-as="rule"
                    placeholder="Type"
                    autocomplete="off"
                    @change="setUnit(rule)"
                  />
                </v-col>
                <v-col
                  cols="5"
                  class="py-0"
                >
                  <v-text-field
                    v-model="rule.threshold_value"
                    v-validate="'required|min_value:0'"
                    :min="0"
                    :name="'threshold_value' + counter"
                    :error-messages="errors.collect('threshold_value' + counter)"
                    :disabled="!!claimAssignee.employee.end_date"
                    data-vv-delay="500"
                    data-vv-as="value"
                    label="Value"
                    autocomplete="off"
                    type="number"
                    :prefix="rule.type === 'Amount' ? 'is more than RM ' : 'is more than '"
                    @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                  />
                </v-col>
                <v-col
                  cols="2"
                  class="py-0"
                >
                  <v-text-field
                    v-model="rule.threshold_unit"
                    v-validate="'required'"
                    :name="'threshold_unit' + counter"
                    :error-messages="errors.collect('threshold_unit' + counter)"
                    data-vv-delay="500"
                    data-vv-as="unit"
                    label="Unit"
                    readonly
                    disabled
                  />
                </v-col>

                <v-col
                  cols="2"
                  class="text-center"
                >
                  <v-icon
                    v-if="!claimAssignee.employee.end_date"
                    color="error"
                    @click="deleteRow(counter)"
                  >
                    mdi-window-close
                  </v-icon>
                </v-col>
              </v-row>
            </v-col>

            <v-col
              cols="12"
              class="py-0 my-0"
            >
              <v-autocomplete
                v-model="selectedCompanies"
                v-validate="'required'"
                :loading="loading"
                label="Companies"
                item-text="name"
                item-value="id"
                :items="companies"
                :disabled="!!claimAssignee.employee.end_date"
                clearable
                multiple
                hide-selected 
                chips
                small-chips
                deletable-chips
                return-object
                :error-messages="errors.collect('selectedCompanies')"
                data-vv-name="selectedCompanies"  
                data-vv-as="companies"
              />
            </v-col>

            <v-col
              cols="12"
              class="py-0"
            >
              <v-checkbox
                v-model="localClaimAssignee.is_final_approver"
                :disabled="!!claimAssignee.employee.end_date"
                color="green"
                label="Is final approver?"
              />
              <div class="text-caption text--secondary ml-8 mt-n6">
                Check this box to designate this claim assignee as the final approver for the assigned companies.
              </div>
            </v-col>
          </v-row>
        </v-container>
      </form>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          :disabled="!!claimAssignee.employee.end_date"
          color="hrissecondary"
          class="white--text"
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      claimAssignee: {
        type: Object,
        required: true,
      },
      companies: {
        type: Array,
        required: true,
      },
      departments: {
        type: Array,
        required: true,
      },
      employees: {
        type: Array,
        required: true,
      },
      claimApprovalWorkflowBatch: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        loading: false,
        progressing: false,
        localClaimAssignee: null,
        types: ['Amount', 'Date validity'],
        filteredEmployees: [],
        workflow: null,
        approvalLevel: null,
      }
    },
    computed: {
      thresholdTypes () {
        return (counter) => {
          const selectedTypes = this.localClaimAssignee.claim_threshold_rules
            .map((rule, item) => item !== counter ? rule.type : null)
            .filter(type => type)

          return this.types.filter(type => !selectedTypes.includes(type))
        }
      },
      selectedCompanies: {
        get () {
          if (
            !this.localClaimAssignee ||
            !Array.isArray(this.localClaimAssignee.claim_assignee_companies) ||
            !Array.isArray(this.companies)
          ) {
            return []
          }

          const assigned = this.localClaimAssignee.claim_assignee_companies.filter(item => {
            return !item.end_date || this.$moment(item.end_date).isSameOrAfter(this.$moment(), 'day')
          })
          const actualCompanies = this.companies.filter(c => c.id !== 'ALL')

          const assignedCompanies = assigned.map(item => item.company)

          const assignedIds = assignedCompanies.map(c => String(c.id))
          const actualIds = actualCompanies.map(c => String(c.id))

          const isAllSelected = assignedIds.length === actualIds.length &&
            actualIds.every(id => assignedIds.includes(id))

          return isAllSelected
            ? [this.companies.find(c => c.id === 'ALL')]
            : assignedCompanies
        },
        set (newValue) {
          const actualCompanies = this.companies.filter(c => c.id !== 'ALL')
          const selectedIds = newValue.map(c => c.id)

          const isAllSelected = selectedIds.includes('ALL')
          const hasOtherCompanies = selectedIds.some(id => id !== 'ALL')

          let selectedCompanies = []

          if (isAllSelected && !hasOtherCompanies) {
            selectedCompanies = actualCompanies
          } else if (isAllSelected && hasOtherCompanies) {
            const last = newValue[newValue.length - 1]
            if (last.id === 'ALL') {
              selectedCompanies = actualCompanies
            } else {
              selectedCompanies = [last]
            }
          } else {
            selectedCompanies = newValue
          }

          this.localClaimAssignee.claim_assignee_companies = selectedCompanies.map(company => ({ company }))
        },
      },
    },
    watch: {
      dialog: function (value) {
        if (!value) return
        this.loading = true

        this.localClaimAssignee = {
          ...this.claimAssignee,
          claim_threshold_rules: JSON.parse(JSON.stringify(this.claimAssignee.claim_threshold_rules)),
        }

        this.approvalLevel = this.localClaimAssignee.approval_level

        this.loading = false
        
        let scope = this
        this.$validator.extend('cannotBeSuperiorLevel', {
          getMessage () {
            return 'Cannot enter same level as superior\'s approval level'
          },
          validate (value) {
            const numValue = Number(value)
            const maxLevel = Number(scope.claimApprovalWorkflowBatch.superior_approval_level)

            return numValue !== maxLevel
          },
        })
        
        this.$validator.extend('maxValue', {
          getMessage () {
            return 'Cannot enter level more than current workflow batch maximum approval level'
          },
          validate (value) {
            const numValue = Number(value)
            const maxLevel = Number(scope.claimApprovalWorkflowBatch.total_level_of_approval)

            return numValue <= maxLevel
          },
        })
      },
      approvalLevel: {
        immediate: true,
        handler (value) {
          if (
            value && (Number(value) !== Number(this.claimApprovalWorkflowBatch.superior_approval_level)) &&
            (Number(value) <= Number(this.claimApprovalWorkflowBatch.total_level_of_approval))
          ) {
            if (this.claimAssignee.employee.end_date) {
              this.filteredEmployees = [this.claimAssignee.employee]
            } else {
              this.workflow = this.claimApprovalWorkflowBatch.claim_approval_workflows.find(workflow => {
                return Number(workflow.approval_level) === Number(value)
              })

              if (this.workflow.approvable_type === 'App\\Model\\Department') {
                this.filteredEmployees = this.employees.filter(employee => { 
                  return Number(employee.department_id) === Number(this.workflow.approvable_id)
                })
              } else if (this.workflow.approvable_type === 'App\\Model\\Position') {
                this.filteredEmployees = this.employees.filter(employee => { 
                  return Number(employee.current_position.position_id) === Number(this.workflow.approvable_id)
                })
              } else { // specific employee
                this.filteredEmployees = this.employees.filter(employee => { 
                  return employee.employee_uid === this.workflow.approvable_id
                })

                this.assignee = this.filteredEmployees[0].employee_uid
              }
            }
          } else {
            this.filteredEmployees = []
          }
        },
      },
    },
    methods: {
      setUnit (rule) {
        if (rule.type === 'Amount') {
          rule.threshold_unit = 'RM'
        } else {
          rule.threshold_unit = 'days'
        }
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 
        
            this.progressing = true

            this.$api.claimAssignees().update(this.localClaimAssignee.id, this.prepareFormData())
              .then(res => {
                if (res.status === 200 || res.status === 201) {
                  this.$emit('updated')              
                  this.$snack({ message: 'Claim assignee updated' })
                  this.$resetData()
                  this.dialog = false                 
                }
              })
              .catch((error) => {
                if (error.response.status === 422) {
                  if (this.localClaimAssignee.enable_threshold && this.localClaimAssignee.claim_threshold_rules.length <= 0) {
                    this.addRow()
                  }

                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.message, 
                  })
                } else {
                  this.$snack({
                    color: 'error',
                    message: 'Failed to update claim assignee',
                  })
                }
              })
              .then(() => {
                this.progressing = false
              })
          })
      },
      prepareFormData () {
        const selectedIds = this.selectedCompanies.map(company => company.id)

        return {
          approval_level: this.approvalLevel,
          claim_approval_workflow_id: this.workflow.id,
          employee_uid: this.localClaimAssignee.employee_uid,
          enable_threshold: this.localClaimAssignee.enable_threshold,
          is_final_approver: this.localClaimAssignee.is_final_approver,
          rules: this.localClaimAssignee.enable_threshold ? this.localClaimAssignee.claim_threshold_rules : [],
          companies: selectedIds.includes('ALL') ? (
            this.companies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : selectedIds,
        }
      },
      addRow () {
        this.localClaimAssignee.claim_threshold_rules.push({ type: null, threshold_value: null, threshold_unit: '' })
      },
      deleteRow (counter) {
        this.localClaimAssignee.claim_threshold_rules.splice(counter, 1)
      },
    },
  }
</script>
